import React, { useState, useEffect } from 'react';
import { getRightworkAPI, postRightworkAPI } from '../requests/utilities/requests';
import {
  API_get_trash,
  API_restore_item,
  API_permanent_delete_item,
} from '../../../constants';
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RestoreIcon from '@mui/icons-material/Restore';
import FolderIcon from '@mui/icons-material/Folder';
import DescriptionIcon from '@mui/icons-material/Description';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';
import TableChartIcon from '@mui/icons-material/TableChart';
import TextSnippetIcon from '@mui/icons-material/TextSnippet';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import LoadSpinner from '../../../utilities/LoadSpinner';

function TrashView(props) {
  const [trashItems, setTrashItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [permanentDeleteModalOpen, setPermanentDeleteModalOpen] = useState(false);
  const [permanentDeleteTarget, setPermanentDeleteTarget] = useState(null);

  const fetchTrash = async () => {
    try {
      const res = await getRightworkAPI(API_get_trash, {
        company_id: props.homeCompany,
        store_id: props.homeStore,
      });
      if (res.status === 200) {
        setTrashItems(res.data?.redux?.payload?.trash_items || []);
      }
    } catch (error) {
      toast.error("Failed to fetch trash", { autoClose: 3000 });
    }
    setIsLoading(false);
  };

  useEffect(() => {
    fetchTrash();
  }, [props.homeCompany, props.homeStore]);

  const restoreItem = async (item) => {
    try {
      toast.loading("Restoring...", { autoClose: false });
      const res = await postRightworkAPI(API_restore_item, {
        company_id: props.homeCompany,
        item_uuid: item.uuid,
        item_type: item.type,
      });
      if (res.status === 200) {
        toast.dismiss();
        toast.success("Restored successfully", { autoClose: 3000 });
        fetchTrash();
        props.onRestoreComplete();
      }
    } catch (error) {
      toast.dismiss();
      if (error.response?.data?.system?.message) {
        toast.error(error.response?.data?.system?.message, { autoClose: 3000 });
      } else {
        toast.error("Failed to restore", { autoClose: 3000 });
      }
    }
  };

  const requestPermanentDelete = (item) => {
    setPermanentDeleteTarget(item);
    setPermanentDeleteModalOpen(true);
  };

  const confirmPermanentDelete = async () => {
    const item = permanentDeleteTarget;
    setPermanentDeleteModalOpen(false);
    setPermanentDeleteTarget(null);

    try {
      toast.loading("Permanently deleting...", { autoClose: false });
      const res = await postRightworkAPI(API_permanent_delete_item, {
        company_id: props.homeCompany,
        item_uuid: item.uuid,
        item_type: item.type,
      });
      if (res.status === 200) {
        toast.dismiss();
        toast.success("Permanently deleted", { autoClose: 3000 });
        fetchTrash();
        if (props.onTrashChanged) props.onTrashChanged();
      }
    } catch (error) {
      toast.dismiss();
      if (error.response?.data?.system?.message) {
        toast.error(error.response?.data?.system?.message, { autoClose: 3000 });
      } else {
        toast.error("Failed to delete", { autoClose: 3000 });
      }
    }
  };

  const FileIcon = ({ type, extension }) => {
    if (type === 'folder') {
      return <FolderIcon className="w-5 h-5 text-violet-700" />;
    }
    switch (extension) {
      case 'jpeg':
      case 'jpg':
      case 'png':
      case 'svg':
        return <ImageIcon className="w-5 h-5 text-orange-500" />;
      case 'pdf':
        return <PictureAsPdfIcon className="w-5 h-5 text-red-500" />;
      case 'xls':
      case 'xlsx':
      case 'csv':
        return <TableChartIcon className="w-5 h-5 text-green-500" />;
      case 'doc':
      case 'docx':
        return <DescriptionIcon className="w-5 h-5 text-blue-500" />;
      case 'txt':
        return <TextSnippetIcon className="w-5 h-5 text-gray-500" />;
      default:
        return <DescriptionIcon className="w-5 h-5 text-gray-500" />;
    }
  };

  const formatDeletedDate = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <LoadSpinner />
      </div>
    );
  }

  return (
    <div className="p-6">
      <p className="text-sm text-gray-500 mb-6">
        Items in trash are automatically deleted after 30 days.
      </p>

      {trashItems.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12">
          <DeleteOutlineIcon style={{ fontSize: 48, color: '#D1D5DB' }} />
          <p className="text-gray-500 text-lg mt-4">Trash is empty</p>
        </div>
      ) : (
        <div className="space-y-2">
          {trashItems.map((item) => (
            <div
              key={item.uuid}
              className="flex items-center justify-between p-4 border rounded-lg bg-white hover:bg-gray-50"
            >
              <div className="flex items-center space-x-3 min-w-0 flex-1">
                <FileIcon type={item.type} extension={item.extension} />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {item.displayName || item.name}
                  </p>
                  <p className="text-xs text-gray-500">
                    {item.type === 'folder' ? 'Folder' : item.fileSize || 'File'}
                    {' '}&middot;{' '}
                    Deleted {formatDeletedDate(item.deleted_at)}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-2 ml-4">
                <button
                  onClick={() => restoreItem(item)}
                  className="flex items-center space-x-1 px-3 py-1.5 text-sm text-violet-700 hover:bg-violet-50 rounded-lg"
                >
                  <RestoreIcon style={{ fontSize: 16 }} />
                  <span>Restore</span>
                </button>
                <button
                  onClick={() => requestPermanentDelete(item)}
                  className="flex items-center space-x-1 px-3 py-1.5 text-sm text-rose-700 hover:bg-rose-50 rounded-lg"
                >
                  <DeleteOutlineIcon style={{ fontSize: 16 }} />
                  <span>Delete</span>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <DeleteConfirmationModal
        open={permanentDeleteModalOpen}
        handleClose={() => {
          setPermanentDeleteModalOpen(false);
          setPermanentDeleteTarget(null);
        }}
        handleConfirm={confirmPermanentDelete}
        itemName={permanentDeleteTarget?.displayName || permanentDeleteTarget?.name || ''}
        itemType={permanentDeleteTarget?.type || 'file'}
        hasContents={permanentDeleteTarget?.type === 'folder'}
        permanent={true}
      />
    </div>
  );
}

export default TrashView;
